"use client";
import { useEffect, useMemo, useRef, useState } from "react";
import VideoPlayer from "../video/VideoPlayer";
import MediaViewer from "../common/MediaViewer";
import ImageLoader from "../common/ImageLoader";

const Carousel = ({
  media,
  enablePreview = true,
  autoscroll = false,
  hideThumb = false,
  scrollInterval = 4000,
  inMarketPlace = false,
  imgRes = "500",
  zoomImgRes = "800",
}) => {
  const getHighRes = (url) =>
    url
      ?.replace?.("r200x200", `r${imgRes}x${imgRes}`)
      ?.replace?.("v200x200", `v${imgRes}x${imgRes}`);
  const getZoomRes = (url) =>
    url
      ?.replace?.("r200x200", `r${zoomImgRes}x${zoomImgRes}`)
      ?.replace?.("v200x200", `v${zoomImgRes}x${zoomImgRes}`);

  const [showMediaViewer, setShowMediaViewer] = useState(false);

  const userInitiatedRef = useRef(false);
  const mainStripRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);
  const intersectionRef = useRef(null);

  // touch/swipe refs
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchCurrentXRef = useRef(0);
  const startScrollLeftRef = useRef(0);
  const isSwipingRef = useRef(false);

  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const [lastMediaKey, setLastMediaKey] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const thumbsRef = useRef([]);
  const mainRef = useRef([]);

  const validMedia = useMemo(
    () =>
      (media || [])
        .filter((item) => item && (item.mediaThumb || item.mediaUrl))
        .map((item) => {
          const baseUrl =
            item?.mediaThumb && typeof item.mediaThumb === "string"
              ? item.mediaThumb
              : item.mediaUrl;

          const highResUrl = getHighRes(baseUrl);
          const zoomUrl = getZoomRes(baseUrl);

          return {
            ...item,
            highResUrl,
            // ✅ Only include a distinct zoom URL if it’s different
            zoomUrl: zoomUrl !== highResUrl ? zoomUrl : undefined,
          };
        }),
    [media]
  );

  // Sync loader arrays to rendered items
  useEffect(() => {
    const len = validMedia.length;
    setSelectedIndex(0);
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  }, [validMedia]);

  // IntersectionObserver: only run autoscroll when carousel is visible
  useEffect(() => {
    const node = mainStripRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    intersectionRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting && entry.intersectionRatio > 0);
        });
      },
      { threshold: [0, 0.25, 0.5, 1] }
    );

    intersectionRef.current.observe(node);

    return () => {
      if (intersectionRef.current && node) {
        intersectionRef.current.unobserve(node);
      }
      intersectionRef.current = null;
    };
  }, []);

  // helper: horizontally center a slide inside the main strip
  const scrollToIndex = (index, behavior = "smooth") => {
    const container = mainStripRef.current;
    const el = mainRef.current[index];
    if (!container || !el) return;
    const containerCenter = container.clientWidth / 2;
    const elCenter = el.offsetLeft + el.clientWidth / 2;
    const newScrollLeft = Math.max(0, elCenter - containerCenter);
    container.scrollTo({ left: newScrollLeft, behavior });
  };

  // Swiping helpers
  const beginSwipe = (touchX, touchY) => {
    const container = mainStripRef.current;
    if (!container) return;
    isSwipingRef.current = false;
    touchStartXRef.current = touchX;
    touchStartYRef.current = touchY;
    touchCurrentXRef.current = touchX;
    startScrollLeftRef.current = container.scrollLeft;
  };

  const moveSwipe = (touchX, touchY) => {
    const container = mainStripRef.current;
    if (!container) return;
    const dx = touchX - touchStartXRef.current;
    const dy = touchY - touchStartYRef.current;

    if (!isSwipingRef.current) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dx) > Math.abs(dy)) {
        isSwipingRef.current = true;
      } else {
        isSwipingRef.current = false;
        return;
      }
    }

    touchCurrentXRef.current = touchX;
    container.scrollLeft =
      startScrollLeftRef.current - (touchX - touchStartXRef.current);
  };

  const endSwipe = () => {
    const container = mainStripRef.current;
    if (!container) return;
    if (!isSwipingRef.current) {
      scrollToIndex(selectedIndex, "smooth");
      return;
    }

    const dx = touchCurrentXRef.current - touchStartXRef.current;
    const threshold = Math.max(50, container.clientWidth * 0.08);
    if (dx > threshold) {
      userInitiatedRef.current = true;
      setSelectedIndex((prev) => {
        const next = prev === 0 ? validMedia.length - 1 : prev - 1;
        requestAnimationFrame(() => scrollToIndex(next, "smooth"));
        return next;
      });
    } else if (dx < -threshold) {
      userInitiatedRef.current = true;
      setSelectedIndex((prev) => {
        const next = prev === validMedia.length - 1 ? 0 : prev + 1;
        requestAnimationFrame(() => scrollToIndex(next, "smooth"));
        return next;
      });
    } else {
      scrollToIndex(selectedIndex, "smooth");
    }
    isSwipingRef.current = false;
  };

  // Scroll thumbnail and main strip when user initiates
  useEffect(() => {
    if (hideThumb) return;
    if (!userInitiatedRef.current) return;
    const el = thumbsRef.current[selectedIndex];
    if (el)
      el.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      });
    userInitiatedRef.current = false;
  }, [selectedIndex, hideThumb]);

  useEffect(() => {
    if (!userInitiatedRef.current) return;
    requestAnimationFrame(() => scrollToIndex(selectedIndex));
    userInitiatedRef.current = false;
  }, [selectedIndex]);

  // Track last media key
  useEffect(() => {
    const key = JSON.stringify(
      (media || []).map((m) => m?.mediaThumb || m?.mediaUrl)
    );
    if (key === lastMediaKey) return;
    setLastMediaKey(key);
  }, [media, lastMediaKey]);

  // Prev / Next
  const goPrev = () => {
    if (!validMedia.length) return;
    const newIndex =
      selectedIndex === 0 ? validMedia.length - 1 : selectedIndex - 1;
    userInitiatedRef.current = true;
    setSelectedIndex(newIndex);
    requestAnimationFrame(() => scrollToIndex(newIndex));
    restartAutoScrollInterval();
  };

  const goNext = () => {
    if (!validMedia.length) return;
    const newIndex =
      selectedIndex === validMedia.length - 1 ? 0 : selectedIndex + 1;
    userInitiatedRef.current = true;
    setSelectedIndex(newIndex);
    requestAnimationFrame(() => scrollToIndex(newIndex));
    restartAutoScrollInterval();
  };

  // Thumbnail & main load handlers

  const onThumbClick = (index) => {
    userInitiatedRef.current = true;
    setSelectedIndex(index);
    requestAnimationFrame(() => scrollToIndex(index));
    restartAutoScrollInterval();
  };
  const onThumbHover = (index) => {
    userInitiatedRef.current = true;
    setSelectedIndex(index);
    requestAnimationFrame(() => scrollToIndex(index));
    restartAutoScrollInterval();
  };

  // AUTOSCROLL helpers

  const startAutoScrollInterval = (intervalMs = scrollInterval) => {
    stopAutoScrollInterval();
    if (!autoscroll || validMedia.length <= 1 || !isVisible || isHovered)
      return;
    autoScrollIntervalRef.current = setInterval(() => {
      userInitiatedRef.current = true;
      setSelectedIndex((prev) => {
        const next = prev >= validMedia.length - 1 ? 0 : prev + 1;
        requestAnimationFrame(() => scrollToIndex(next));
        return next;
      });
    }, intervalMs);
  };

  const stopAutoScrollInterval = () => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  const restartAutoScrollInterval = () => {
    if (!autoscroll) return;
    stopAutoScrollInterval();
    autoScrollIntervalRef.current = setTimeout(
      () => startAutoScrollInterval(scrollInterval),
      scrollInterval
    );
  };

  useEffect(() => {
    if (!autoscroll || !isVisible || isHovered || validMedia.length <= 1)
      return stopAutoScrollInterval();
    startAutoScrollInterval(scrollInterval);
    return () => stopAutoScrollInterval();
  }, [autoscroll, validMedia.length, isVisible, isHovered, scrollInterval]);

  useEffect(() => () => stopAutoScrollInterval(), []);

  if (!validMedia.length) return null;

  // Touch handlers
  const handleTouchStart = (e) => {
    if (!e.touches || !e.touches.length) return;
    const t = e.touches[0];
    beginSwipe(t.clientX, t.clientY);
  };
  const handleTouchMove = (e) => {
    if (!e.touches || !e.touches.length) return;
    const t = e.touches[0];
    moveSwipe(t.clientX, t.clientY);
  };
  const handleTouchEnd = () => endSwipe();

  return (
    <>
      <div
        className="gallery-container"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="main-image-wrapper">
          {validMedia.length > 1 && !autoscroll && (
            <div className="modal-arrow-div left" onClick={goPrev}>
              <button className="modal-arrow left">&#10094;</button>
            </div>
          )}
          <div
            className="media-main-strip main-modal"
            ref={mainStripRef}
            style={{
              display: "flex",
              overflowX: "auto",
              overflowY: "hidden",
              scrollBehavior: "smooth",
              alignItems: "center",
              WebkitOverflowScrolling: "touch",
              touchAction: "pan-y",
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
          >
            {validMedia.map((item, index) => (
              <div
                key={item._id || `${index}-${item.mediaName}`}
                ref={(el) => (mainRef.current[index] = el)}
                className={`media-main-item ${
                  index === selectedIndex ? "active" : ""
                } ${item.mediaType === "video" ? "media-video-item" : ""} ${
                  inMarketPlace ? "mediaInMarket" : ""
                }`}
                onClick={() => setShowMediaViewer(true)}
              >
                {item.mediaType === "image" && (
                  <>
                    <ImageLoader
                      media={item}
                      zoom={true}
                      alt={item.name}
                      skeletonStyle={{
                        width: "100%",
                        borderRadius: "8px",
                        height: "30rem",
                      }}
                    />
                  </>
                )}

                {item.mediaType === "video" && (
                  <>
                    <VideoPlayer
                      src={item.mediaUrl}
                      className="media-main-video"
                      skeletonStyle={{ width: "100%", minHeight: "30rem" }}
                      autoPlay={true}
                      continuousPlay={true}
                    />
                  </>
                )}
                {item.mediaType === "file" && (
                  <>
                    <ImageLoader
                      media={item}
                      alt={item.name}
                      skeletonStyle={{
                        width: "100%",
                        borderRadius: "8px",
                        height: "30rem",
                      }}
                    />
                  </>
                )}
              </div>
            ))}
          </div>
          {validMedia.length > 1 && !autoscroll && (
            <div className="modal-arrow-div right" onClick={goNext}>
              <button className="modal-arrow right">&#10095;</button>
            </div>
          )}
        </div>

        {/* Thumbnails */}
        {validMedia.length > 1 && !hideThumb && (
          <div className="image-gallery-wrapper">
            <div className="image-gallery">
              {validMedia.map((item, index) => (
                <div
                  key={item._id || `${index}-${item.mediaName}`}
                  ref={(el) => (thumbsRef.current[index] = el)}
                  role="button"
                  aria-label={`View image ${index + 1}`}
                  className={`gallery-item ${
                    index === selectedIndex ? "active-thumbnail" : ""
                  }`}
                  onClick={() => onThumbClick(index)}
                  onMouseEnter={() => onThumbHover(index)}
                >
                  <ImageLoader
                    media={item}
                    skeletonHeight={60}
                    skeletonWidth={60}
                    alt={item.name}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showMediaViewer && enablePreview && (
        <MediaViewer
          media={validMedia}
          setShowMediaViewer={setShowMediaViewer}
        />
      )}
    </>
  );
};

export default Carousel;
